/**
 * Formata organizacije za zaslone brez projektnega konteksta (T1216).
 *
 * BoQ bere `project.effective_date_format` / `effective_decimal_format`, ki ju
 * izračuna zaledje. Tržnica projekta nima, zato mora verigo privzetkov
 * **organizacija → sistem → slovenski privzetek** sestaviti odjemalec. Prazen
 * niz je pri obeh poljih veljavna vrednost in pomeni »podeduj sistemsko«
 * (`Organization.date_format` je `null=True, blank=True`), zato branje
 * `currentOrganization.date_format` naravnost ne zadošča — in prav to je bil
 * izvor prijave: modul je datume izrisoval na štiri načine, nobeden ni bral
 * nastavitve.
 *
 * Kavelj in ne funkcija, ker mora izris slediti menjavi organizacije.
 */
import { useMemo } from 'react';

import { currencySymbol, MARKETPLACE_CURRENCY } from '../lib/currency';
import { formatDate as formatDateRaw, formatNumber } from '../lib/formatters';
import { useOrganizationStore } from '../store/organizationStore';
import { useSystemStore } from '../store/systemStore';

import { parseMoneyInput, type DecimalFormat } from '../lib/moneyInput';

// T1796: razčlemba zneska je preseljena v `lib/moneyInput.ts`, da jo smejo
// uporabiti tudi moduli brez Reacta (npr. `lib/scenarioAmount.ts`). Tu ostaja
// izvoz naprej, ker je bila doslej del javnega obraza tega modula.
export { parseMoneyInput };
export type { DecimalFormat };

export type DateFormatKey = 'DD.MM.YYYY' | 'YYYY-MM-DD' | 'MM/DD/YYYY';

const DATE_FALLBACK: DateFormatKey = 'DD.MM.YYYY';
const DECIMAL_FALLBACK: DecimalFormat = 'comma';

/** Cene v Tržnici nimajo stolpca za valuto — model jih vodi v EUR (oznaka
 *  polja je »Cena (EUR)«), zato je simbol tu konstanta in ne `org.currency`.
 *  T2060 (odločitev Alena, 26.9.2026): **tržnica je samo v EUR** — simbol je
 *  vedno €, ločila pa po organizaciji gledalca. Isto pravilo velja za pošto
 *  (`apps/marketplace/services/notify.py`, `MARKETPLACE_CURRENCY`).
 *  Ko modul dobi več valut, se popravi tukaj in nikjer drugje. */
const CURRENCY_SYMBOL = currencySymbol(MARKETPLACE_CURRENCY);


export interface OrgFormats {
  dateFormat: DateFormatKey;
  decimalFormat: DecimalFormat;
  /** Datum brez ure, v zapisu organizacije. */
  formatDate: (value: string | null | undefined) => string;
  /** Datum in ura **brez sekund** — sekunde v roku nikomur nič ne povedo. */
  formatDateTime: (value: string | null | undefined) => string;
  /** Znesek s tisočicami in valuto, v zapisu organizacije. */
  formatMoney: (value: string | number | null | undefined) => string;
  /** Uporabnikov vnos → oblika za API; `null`, kadar to ni znesek. */
  parseMoney: (raw: string) => string | null;
  /** Namig v vnosnem polju, v zapisu organizacije. */
  moneyPlaceholder: string;
}

export function useOrgFormats(): OrgFormats {
  const orgDate = useOrganizationStore((s) => s.currentOrganization?.date_format);
  const orgDecimal = useOrganizationStore(
    (s) => s.currentOrganization?.decimal_format,
  );
  const sysDate = useSystemStore((s) => s.settings?.date_format);
  const sysDecimal = useSystemStore((s) => s.settings?.decimal_format);

  return useMemo(() => {
    const dateFormat = (orgDate || sysDate || DATE_FALLBACK) as DateFormatKey;
    const decimalFormat = (orgDecimal ||
      sysDecimal ||
      DECIMAL_FALLBACK) as DecimalFormat;

    const formatDate = (value: string | null | undefined) =>
      formatDateRaw(value, dateFormat);

    const formatDateTime = (value: string | null | undefined) => {
      if (!value) return '';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${formatDateRaw(value, dateFormat)} ${hh}:${mm}`;
    };

    const formatMoney = (value: string | number | null | undefined) => {
      const formatted = formatNumber(value, decimalFormat, 2);
      return formatted ? `${formatted} ${CURRENCY_SYMBOL}` : '';
    };

    return {
      dateFormat,
      decimalFormat,
      formatDate,
      formatDateTime,
      formatMoney,
      parseMoney: (raw: string) => parseMoneyInput(raw, decimalFormat),
      moneyPlaceholder: decimalFormat === 'comma' ? '1200,00' : '1200.00',
    };
  }, [orgDate, orgDecimal, sysDate, sysDecimal]);
}
